import * as React from 'react';
import propz from 'propz';
import { History, Location } from 'history';
import { INVITE_STATUS } from '../../../../../../consts/invite';
import { AGE_GROUPS } from '../../../../../../consts/school';
import { GENDER } from '../../../../../../consts/common';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING } from '../../../../../../consts/tournament';
import { SimpleModal } from '../../../../../../components/SimpleModal/SimpleModal';
import { Loader } from '../../../../../../components/Loader/Loader';
import { StatusModal } from '../../../../../../components/StatusModal/StatusModal';
import { TournamentBookingSuccessModal } from 'Src/components/TournamentBookingSuccessModal/TournamentBookingSuccessModal';
import { TournamentWaitingListSuccessModal } from 'Src/components/TournamentWaitingListSuccessModal/TournamentWaitingListSuccessModal';
import { getTeamPlacesLeft, isIndividualTournament } from '../../../../../../helpers/tournament/tournament';
import { addSchoolTournamentTeam } from '../../../../../../helpers/service/admin/tournamentTeams';
import { uniqArray } from '../../../../../../helpers/common/common';
import { getFromHistory } from '../../../../../../helpers/history/history';
import { getTournament } from '../../../../../../helpers/service/adminSU/tournamentsTable';
import {
  acceptAutoEnrollmentInvite,
  acceptInvite,
  getInvites,
  withdrawTournamentBooking
} from 'Src/helpers/service/admin/invites';
import { getSchoolPublic, getTournamentEventsPublic } from '../../../../../../helpers/service/public/public';
import { sendTournamentInvite } from '../../../../../../helpers/service/adminSU/tournamentEligibleSchoolsTable';
import { unsubscribeTournament } from '../../../../../../helpers/service/adminSU/tournamentsTable';
import { SchoolInvite } from '../../../../../../models/invite';
import { AppUser } from '../../../../../../views/App/App';
import { AcceptTournamentInviteWithoutAutoEnrollmentTeams } from '../../../../../../views/GenericView/AdminView/Invites/AcceptTournamentInviteWithoutAutoEnrollmentTeams/AcceptTournamentInviteWithoutAutoEnrollmentTeams';
import { AcceptTournamentInviteWithAutoEnrollmentTeams } from '../../../../../../views/GenericView/AdminView/Invites/AcceptTournamentInviteWithAutoEnrollmentTeams/AcceptTournamentInviteWithAutoEnrollmentTeams';
import './TournamentInvite.css';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}
interface State {
  isLoading: boolean;
  isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen: boolean;
  isAcceptTournamentInviteWithAutoEnrollmentModalOpen: boolean;
  isTournamentInviteWaitingListModalOpen: boolean;
  isTournamentInviteInfoModalOpen: boolean;
  isExistingBookingModalOpen: boolean;
  existingBookingMode: 'PARTICIPANT' | 'WAITING_LIST' | 'REJECTED' | 'REJECTED_PARTICIPANT' | undefined;
  invite: SchoolInvite | undefined;
  isWithdrawWaitingListSuccessModalOpen: boolean;
  isBookingConfirmedAsWaitingList: boolean;
  isNotInUnionModalOpen: boolean;
  notInUnionMessage: string;
  isLoadErrorModalOpen: boolean;
  loadErrorMessage: string;
  tournamentEvents: any[];
  selectedEventIds: string[];
  eventSelectionError: string;
}

export class TournamentInvite extends React.Component<Props, State> {
  private bookingLoadId = 0;

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: true,
      isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen: false,
      isAcceptTournamentInviteWithAutoEnrollmentModalOpen: false,
      isTournamentInviteWaitingListModalOpen: false,
      isTournamentInviteInfoModalOpen: false,
      isExistingBookingModalOpen: false,
      existingBookingMode: undefined,
      invite: undefined,
      isWithdrawWaitingListSuccessModalOpen: false,
      isBookingConfirmedAsWaitingList: false,
      isNotInUnionModalOpen: false,
      notInUnionMessage: '',
      isLoadErrorModalOpen: false,
      loadErrorMessage: '',
      tournamentEvents: [],
      selectedEventIds: [],
      eventSelectionError: ''
    };
  }

  componentDidMount() {
    this.loadBooking();
  }

  componentDidUpdate(prevProps: Props) {
    const previousUser = prevProps.user;
    const currentUser = this.props.user;
    if (
      previousUser.activeSchoolId !== currentUser.activeSchoolId ||
      previousUser.roleSessionKey !== currentUser.roleSessionKey
    ) {
      this.loadBooking();
    }
  }

  componentWillUnmount() {
    this.bookingLoadId += 1;
  }

  loadBooking = () => {
    const bookingLoadId = ++this.bookingLoadId;
    const ensureCurrentLoad = () => {
      if (bookingLoadId !== this.bookingLoadId) throw 'STALE_BOOKING_LOAD';
    };
    const { history, user } = this.props;
    const tournamentId = getFromHistory(history, 'tournament');
    const { activeSchoolId } = user;

    const filter = { where: { tournamentId: tournamentId } };

    this.setState({
      isLoading: true,
      isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen: false,
      isAcceptTournamentInviteWithAutoEnrollmentModalOpen: false,
      isTournamentInviteWaitingListModalOpen: false,
      isTournamentInviteInfoModalOpen: false,
      isExistingBookingModalOpen: false,
      isNotInUnionModalOpen: false,
      isLoadErrorModalOpen: false,
      invite: undefined,
      tournamentEvents: [],
      selectedEventIds: [],
      eventSelectionError: ''
    });

    const promises = [getTournament(user, tournamentId), getInvites(user, filter)];

    let invites = [];
    let tournament;

    Promise.all(promises)
      .then(async ([_tournament, _invites]) => {
        ensureCurrentLoad();
        // The generic invite endpoint returns both incoming and outgoing invites.
        // Online booking must only ever resume an invitation addressed to the
        // currently active school; otherwise changing role/account can open an
        // unrelated union-side invite and the following action is correctly rejected.
        invites = (_invites || []).filter(invite => {
          const invitedSchoolId = propz.get(invite, ['invitedSchoolId', 'id']) || invite.invitedSchoolId;
          return String(invitedSchoolId || '') === String(activeSchoolId);
        });
        tournament = _tournament;
        if (isIndividualTournament(tournament)) {
          const organiserSchoolId = tournament.schoolUnionId || tournament.schoolId;
          const events = await getTournamentEventsPublic(organiserSchoolId, tournamentId, {
            where: {}, skip: 0, limit: 3000, order: 'startTime ASC'
          });
          ensureCurrentLoad();
          this.setState({
            tournamentEvents: events || [],
            selectedEventIds: (events || []).map(event => String(event.id || event._id))
          });
        }
        if (invites.length === 0) {
          //send invite yourself
          const { schoolId } = tournament;
          return getSchoolPublic(schoolId);
        } else {
          return Promise.resolve();
        }
      })
      .then(schoolUnion => {
        ensureCurrentLoad();
        if (typeof schoolUnion === 'undefined') {
          return Promise.resolve();
        } else {
          const isSchoolConsistInSU = schoolUnion.schoolIds.some(
            schoolIdInSchoolUnion => schoolIdInSchoolUnion === activeSchoolId
          );

          if (!isSchoolConsistInSU) {
            const unionName = schoolUnion.name;
            const organisersEmail = tournament.organisersEmail;
            const emailLine = organisersEmail
              ? ` If you believe this is an error, please contact them at ${organisersEmail}.`
              : ` If you believe this is an error, please contact the organiser directly.`;

            this.setState({
              isLoading: false,
              isNotInUnionModalOpen: true,
              notInUnionMessage: `Your school cannot register for this tournament because it has not been added to ${unionName}.${emailLine}`
            });
            return Promise.reject('NOT_IN_UNION');
          }

          return sendTournamentInvite(
            user,
            tournamentId,
            {
              invitedSchoolId: activeSchoolId,
              inviteSource: 'ONLINE_BOOKING'
            },
            schoolUnion.id
          );
        }
      })
      .then(invite => {
        ensureCurrentLoad();
        const inviteListSource = typeof invite !== 'undefined' ? [invite] : invites;
        const inviteList = inviteListSource.map(inviteItem => ({
          ...inviteItem,
          tournament: inviteItem.tournament || tournament,
          tournamentId: inviteItem.tournamentId || tournament.id
        }));

        const notAcceptedInvites = inviteList.filter(invite => invite.status === INVITE_STATUS.NOT_READY);
        const tournamentSchoolIds = (tournament.schoolIds || []).map(schoolIdObj => schoolIdObj.schoolId);
        const isSchoolTournamentParticipant = tournamentSchoolIds.some(
          tournamentSchoolId => tournamentSchoolId === activeSchoolId
        );

        const acceptedInvites = inviteList.filter(inviteItem => inviteItem.status === INVITE_STATUS.ACCEPTED);

        const rejectedInvites = inviteList.filter(inviteItem => inviteItem.status === INVITE_STATUS.REJECTED);

        switch (true) {
          // show invite
          case notAcceptedInvites.length > 0: {
            const [firstInvite] = inviteList;

            const isAutoEnrollmentTeams = propz.get(firstInvite, ['tournament', 'isAutoEnrollmentTeams'], false);

            if (isAutoEnrollmentTeams) {
              this.setState({
                isLoading: false,
                isAcceptTournamentInviteWithAutoEnrollmentModalOpen: true,
                invite: firstInvite
              });
            } else {
              this.setState({
                isLoading: false,
                isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen: true,
                invite: firstInvite
              });
            }

            break;
          }
          // school in waiting list

          case acceptedInvites.length > 0 && isSchoolTournamentParticipant: {
            const [firstInvite] = acceptedInvites;

            this.setState({
              isLoading: false,
              isExistingBookingModalOpen: true,
              existingBookingMode: 'PARTICIPANT',
              invite: firstInvite
            });
            break;
          }

          case acceptedInvites.length > 0 && !isSchoolTournamentParticipant: {
            const [firstInvite] = acceptedInvites;

            this.setState({
              isLoading: false,
              isExistingBookingModalOpen: true,
              existingBookingMode: 'WAITING_LIST',
              invite: firstInvite
            });
            break;
          }

          case rejectedInvites.length > 0 && !isSchoolTournamentParticipant: {
            const [firstInvite] = rejectedInvites;
            const isAutoEnrollmentTeams = propz.get(firstInvite, ['tournament', 'isAutoEnrollmentTeams'], false);

            if (isAutoEnrollmentTeams) {
              this.setState({
                isLoading: false,
                isAcceptTournamentInviteWithAutoEnrollmentModalOpen: true,
                invite: firstInvite
              });
            } else {
              this.setState({
                isLoading: false,
                isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen: true,
                invite: firstInvite
              });
            }

            break;
          }

          case rejectedInvites.length > 0 && isSchoolTournamentParticipant: {
            const [firstInvite] = rejectedInvites;

            this.setState({
              isLoading: false,
              isExistingBookingModalOpen: true,
              existingBookingMode: 'REJECTED_PARTICIPANT',
              invite: firstInvite
            });
            break;
          }

          case !isSchoolTournamentParticipant:
            history.push({
              pathname: '/calendar'
            });
            break;
          // show tournament teams
          default:
            history.push({
              pathname: `/tournaments/teams`,
              search: `tournamentId=${tournamentId}`
            });
            break;
        }
      })
      .catch(err => {
        if (err === 'NOT_IN_UNION' || err === 'STALE_BOOKING_LOAD') return;
        console.error(err);
        const message = propz.get(err, ['response', 'data', 'details', 'text'], '') ||
          propz.get(err, ['response', 'data', 'message'], '') ||
          'We could not open this tournament booking. Please try again or return to your dashboard.';
        this.setState({ isLoading: false, isLoadErrorModalOpen: true, loadErrorMessage: message });
      });
  };

  getEventSelectionPayload = () => ({ eventIds: this.state.selectedEventIds });

  onBookingError = (error: any): void => {
    const message = propz.get(error, ['response', 'data', 'details', 'text'], '') ||
      propz.get(error, ['response', 'data', 'message'], '') ||
      'We could not complete this booking. Please try again.';
    this.setState({ isLoading: false, isLoadErrorModalOpen: true, loadErrorMessage: message });
  };

  canSubmitEventSelection = (): boolean => {
    if (this.state.tournamentEvents.length > 0 && this.state.selectedEventIds.length === 0) {
      this.setState({ eventSelectionError: 'Select at least one event to continue.' });
      return false;
    }
    return true;
  };

  onEventSelectionChange = (selectedEventIds: string[]): void => {
    this.setState({ selectedEventIds, eventSelectionError: '' });
  };

  getCancelReturnPath = (): string => {
    return String(propz.get(this.props.location, ['state', 'returnTo'], '') || '') || '/calendar';
  };

  onAcceptTournamentInviteWithoutAutoEnrollmentTeamsClick = () => {
    if (!this.canSubmitEventSelection()) return;
    const { invite } = this.state;

    if (!invite || !invite.tournament) {
      this.setState({
        isLoading: false
      });
      return;
    }

    const { user, history } = this.props;

    this.setState({
      isLoading: true
    });

    acceptInvite(user, invite.id, this.getEventSelectionPayload()).then(res => {
      this.setState({
        isLoading: false,
        isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen: false,
        isTournamentInviteInfoModalOpen: true,
        isBookingConfirmedAsWaitingList: true
      });
    }).catch(this.onBookingError);
  };

  onAcceptTournamentInviteWithAutoEnrollmentTeamsClick = (data?) => {
    if (!this.canSubmitEventSelection()) return;
    const { user, history } = this.props;
    const { activeSchoolId, activeSchoolName } = user;

    const { invite } = this.state;

    if (!invite || !invite.tournament || !invite.tournamentId) {
      this.setState({
        isLoading: false
      });
      return;
    }

    const { tournament, id: inviteId, tournamentId } = invite;
    const { autoEnrollmentTeamsSettings, gender: tournamentGender, ages, sportId } = tournament;

    const isAutoEnrollmentToWaitingList = propz.get<boolean>(tournament, ['isAutoEnrollmentToWaitingList'], false);
    const isIndividual = isIndividualTournament(tournament);

    const isMixedTeamsAvailable = propz.get<boolean>(tournament, ['isMixedTeamsAvailable'], false);
    const isGenderMixed = tournamentGender === GENDER.MIXED;
    const gender = isGenderMixed && !isMixedTeamsAvailable ? GENDER.MALE_ONLY : tournamentGender;

    const ageIndex = propz.get(tournament, ['ages', 0], 0);

    const teamPlacesLeft = getTeamPlacesLeft(autoEnrollmentTeamsSettings, ageIndex, gender);

    const autoEnrollmentTeamsSetting = propz.get(autoEnrollmentTeamsSettings, [0]);
    const maximumNumberTeams = propz.get(autoEnrollmentTeamsSetting, ['maximumNumberTeams']);
    const isAllSpacesBooked = isIndividual && maximumNumberTeams === 0 ? false : teamPlacesLeft === 0;

    const isAllGenderAndAgeTeamPlacesLeft = autoEnrollmentTeamsSettings.every(autoEnrollmentTeamsSetting => {
      const maximumNumberTeams = Number(autoEnrollmentTeamsSetting.maximumNumberTeams || 0);
      const currentNumberTeams = Number(autoEnrollmentTeamsSetting.currentNumberTeams || 0);

      return maximumNumberTeams === 0 || maximumNumberTeams - currentNumberTeams > 0;
    });

    const isWaitingListBooking =
      isAutoEnrollmentToWaitingList || (isIndividual ? isAllSpacesBooked : !isAllGenderAndAgeTeamPlacesLeft);

    if (typeof data === 'undefined' && isWaitingListBooking) {
      acceptInvite(user, inviteId, this.getEventSelectionPayload()).then(res => {
        history.push({
          pathname: '/tournaments/waitingList',
          state: {
            showWaitingListJoinedSuccess: true,
            tournamentName: tournament.name
          }
        });
      }).catch(this.onBookingError);
      return;
    }

    let isAllTeamsCountEmpty = true;

    if (typeof data !== 'undefined') {
      for (let key in data) {
        if (key.includes('-students-') || key === 'numberOfStudents') continue;
        const currentTeamsCount = Number(data[key]);
        if (currentTeamsCount !== 0) {
          isAllTeamsCountEmpty = false;
        }
      }
    }

    this.setState({
      isLoading: true
    });

    switch (true) {
      case isAutoEnrollmentToWaitingList:
        acceptInvite(user, inviteId, this.getEventSelectionPayload()).then(res => {
          this.setState({
            isLoading: false,
            isAcceptTournamentInviteWithAutoEnrollmentModalOpen: false,
            isTournamentInviteInfoModalOpen: true,
            isBookingConfirmedAsWaitingList: true
          });
        }).catch(this.onBookingError);
        break;
      case isIndividual && !isAllSpacesBooked:
        acceptAutoEnrollmentInvite(user, inviteId, this.getEventSelectionPayload())
          .then(res => {
            const teamBody = {
              name: activeSchoolName,
              schoolId: activeSchoolId,
              gender: tournamentGender,
              ages: ages,
              sportId: sportId,
              teamType: 'ADHOC',
              ...(Number(tournament.maxPlayersPerTeam) > 0
                ? { numberOfStudents: Number(data && data.numberOfStudents) }
                : {})
            };

            return addSchoolTournamentTeam(user, tournamentId, teamBody);
          })
          .then(res => {
            this.setState({
              isLoading: false,
              isTournamentInviteInfoModalOpen: true
            });
          })
          .catch(this.onBookingError);
        break;
      case isIndividual && isAllSpacesBooked:
        acceptInvite(user, inviteId, this.getEventSelectionPayload()).then(res => {
          this.setState({
            isLoading: false,
            isTournamentInviteWaitingListModalOpen: false,
            isTournamentInviteInfoModalOpen: true,
            isBookingConfirmedAsWaitingList: true
          });
        }).catch(this.onBookingError);
        break;
      case !isAllGenderAndAgeTeamPlacesLeft && isAllTeamsCountEmpty: {
        this.setState({
          isLoading: false,
          isAcceptTournamentInviteWithAutoEnrollmentModalOpen: false,
          isTournamentInviteWaitingListModalOpen: true
        });
        break;
      }
      default:
        const teamsArray: { age: number; gender: string; count: number }[] = [];
        Object.keys(data)
          .filter(key => !key.includes('-students-'))
          .forEach(key => {
            const [ageStr, gender] = key.split('-');
            const currentCount = Number(data[key]);
            if (currentCount > 0) {
              teamsArray.push({
                age: Number(ageStr),
                gender: gender,
                count: currentCount
              });
            }
          });

        let teamName = '';

        const teamsGenderArray = teamsArray.map(team => team.gender);
        const teamsGenderArrayUniq = uniqArray(teamsGenderArray);

        const isGenderMale = tournamentGender === GENDER.MALE_ONLY;
        const isGenderFemale = tournamentGender === GENDER.FEMALE_ONLY;
        const isSomeTeamsWithEqualGender = teamsGenderArray.length !== teamsGenderArrayUniq.length;

        const charArray = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase().split('');
        const teamsDataToPost: any[] = [];

        teamsArray.forEach(team => {
          const teamCount = team.count;
          const teamAge = team.age;
          const teamAgeConverted = AGE_GROUPS['U5-U18'][teamAge];

          switch (true) {
            case isGenderMale:
            case isGenderFemale:
              teamName = `${teamAgeConverted}`;
              if (teamCount === 1) {
                teamsDataToPost.push({
                  name: teamName,
                  schoolId: activeSchoolId,
                  gender: team.gender,
                  ages: [teamAge],
                  isAutoEnrolledTeam: true
                });
              } else {
                for (let i = 0; i < teamCount; i++) {
                  teamName = `${teamAgeConverted} ${charArray[i]}`;
                  teamsDataToPost.push({
                    name: teamName,
                    schoolId: activeSchoolId,
                    gender: team.gender,
                    ages: [teamAge],
                    isAutoEnrolledTeam: true
                  });
                }
              }
              break;

            case !isSomeTeamsWithEqualGender && teamCount === 1:
              teamName = `${teamAgeConverted}`;
              teamsDataToPost.push({
                name: teamName,
                schoolId: activeSchoolId,
                gender: team.gender,
                ages: [teamAge],
                isAutoEnrolledTeam: true
              });
              break;

            case !isSomeTeamsWithEqualGender && teamCount > 1:
              for (let i = 0; i < teamCount; i++) {
                teamName = `${teamAgeConverted} ${charArray[i]}`;
                teamsDataToPost.push({
                  name: teamName,
                  schoolId: activeSchoolId,
                  gender: team.gender,
                  ages: [teamAge],
                  isAutoEnrolledTeam: true
                });
              }
              break;

            case isSomeTeamsWithEqualGender && teamCount === 1:
              teamName = `${teamAgeConverted} ${TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[team.gender]}`;
              teamsDataToPost.push({
                name: teamName,
                schoolId: activeSchoolId,
                gender: team.gender,
                ages: [teamAge],
                isAutoEnrolledTeam: true
              });
              break;

            case isSomeTeamsWithEqualGender && teamCount > 1:
              for (let i = 0; i < teamCount; i++) {
                teamName = `${teamAgeConverted} ${TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[team.gender]} ${
                  charArray[i]
                }`;
                teamsDataToPost.push({
                  name: teamName,
                  schoolId: activeSchoolId,
                  gender: team.gender,
                  ages: [teamAge],
                  isAutoEnrolledTeam: true
                });
              }
              break;
          }
        });

        const nextTeamIndexByCategory: Record<string, number> = {};
        teamsDataToPost.forEach(team => {
          const categoryKey = `${team.ages[0]}-${team.gender}`;
          const teamIndex = nextTeamIndexByCategory[categoryKey] || 0;
          if (Number(tournament.maxPlayersPerTeam) > 0) {
            team.numberOfStudents = Number(data[`${categoryKey}-students-${teamIndex}`]);
          }
          nextTeamIndexByCategory[categoryKey] = teamIndex + 1;
        });

        acceptAutoEnrollmentInvite(user, inviteId, this.getEventSelectionPayload())
          .then(res => Promise.all(teamsDataToPost.map(team => addSchoolTournamentTeam(user, tournamentId, team))))
          .then(() => {
            this.setState({
              isLoading: false,
              isAcceptTournamentInviteWithAutoEnrollmentModalOpen: false,
              isTournamentInviteInfoModalOpen: true,
              isBookingConfirmedAsWaitingList: false
            });
          })
          .catch(this.onBookingError);
    }
  };

  onCancelTournamentInviteWithoutAutoEnrollmentTeamsClick = () => {
    const { history } = this.props;
    history.push({
      pathname: this.getCancelReturnPath()
    });
  };

  onCancelTournamentInviteWithAutoEnrollmentTeamsClick = () => {
    const { history } = this.props;
    history.push({
      pathname: this.getCancelReturnPath()
    });
  };

  onExistingBookingParticipantLeaveClick = () => {
    const { user, history } = this.props;
    const { invite } = this.state;

    if (!invite || !invite.tournamentId) {
      return;
    }

    this.setState({
      isLoading: true
    });

    unsubscribeTournament(user, invite.tournamentId).then(() => {
      history.push({
        pathname: '/calendar'
      });
    });
  };

  onExistingBookingWaitingListLeaveClick = () => {
    const { user, history } = this.props;
    const { invite } = this.state;

    if (!invite || !invite.tournamentId) {
      return;
    }

    this.setState({
      isLoading: true
    });

    withdrawTournamentBooking(user, invite.tournamentId).then(() => {
      this.setState({
        isLoading: false,
        isExistingBookingModalOpen: false,
        isWithdrawWaitingListSuccessModalOpen: true
      });
    });
  };

  renderAcceptTournamentInviteWithoutAutoEnrollmentModal() {
    const { isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen, invite, tournamentEvents, selectedEventIds, eventSelectionError } = this.state;

    if (!invite || !invite.tournament) {
      return null;
    }

    const { user } = this.props;

    return (
      <SimpleModal
        isOpen={isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen}
        showHeader={false}
        customClass="tournamentBookingModalDialog"
        bodyClassName="tournamentBookingModalHost"
        dialogStyle={{ maxWidth: 680 }}
        onBackdropClick={this.onCancelTournamentInviteWithoutAutoEnrollmentTeamsClick}
      >
        <AcceptTournamentInviteWithoutAutoEnrollmentTeams
          onCancel={this.onCancelTournamentInviteWithoutAutoEnrollmentTeamsClick}
          onAccept={this.onAcceptTournamentInviteWithoutAutoEnrollmentTeamsClick}
          invite={invite}
          user={user}
          events={tournamentEvents}
          selectedEventIds={selectedEventIds}
          eventSelectionError={eventSelectionError}
          onEventSelectionChange={this.onEventSelectionChange}
        />
      </SimpleModal>
    );
  }

  renderAcceptTournamentInviteWithAutoEnrollmentModal() {
    const { isAcceptTournamentInviteWithAutoEnrollmentModalOpen, invite, tournamentEvents, selectedEventIds, eventSelectionError } = this.state;

    if (!invite || !invite.tournament) {
      return null;
    }

    const { user } = this.props;

    return (
      <SimpleModal
        isOpen={isAcceptTournamentInviteWithAutoEnrollmentModalOpen}
        showHeader={false}
        customClass="tournamentBookingModalDialog"
        bodyClassName="tournamentBookingModalHost"
        dialogStyle={{ maxWidth: 820 }}
        onBackdropClick={this.onCancelTournamentInviteWithAutoEnrollmentTeamsClick}
      >
        <AcceptTournamentInviteWithAutoEnrollmentTeams
          onCancel={this.onCancelTournamentInviteWithAutoEnrollmentTeamsClick}
          onAccept={this.onAcceptTournamentInviteWithAutoEnrollmentTeamsClick}
          invite={invite}
          user={user}
          events={tournamentEvents}
          selectedEventIds={selectedEventIds}
          eventSelectionError={eventSelectionError}
          onEventSelectionChange={this.onEventSelectionChange}
        />
      </SimpleModal>
    );
  }

  renderExistingBookingModal(): React.ReactNode {
    const { isExistingBookingModalOpen, existingBookingMode, invite } = this.state;
    const { history } = this.props;

    if (!invite || !invite.tournament) {
      return null;
    }

    const { tournament } = invite;

    if (existingBookingMode === 'PARTICIPANT') {
      return (
        <SimpleModal
          isOpen={isExistingBookingModalOpen}
          isBare
          onBackdropClick={() => this.setState({ isExistingBookingModalOpen: false })}
        >
          <section className="tournamentExistingBooking" role="dialog" aria-modal="true" aria-labelledby="existing-booking-title">
            <header>
              <span className="tournamentExistingBooking__icon" aria-hidden="true">✓</span>
              <div>
                <h2 id="existing-booking-title">Already in participant list</h2>
                <p>Your school is already registered for this tournament.</p>
              </div>
              <button type="button" className="tournamentExistingBooking__close" onClick={() => this.setState({ isExistingBookingModalOpen: false })} aria-label="Close">×</button>
            </header>
            <div className="tournamentExistingBooking__body">
              <strong>{tournament.name}</strong>
              <p>Open your tournament teams to continue managing your participation.</p>
            </div>
            <footer>
              <button type="button" className="isDanger" onClick={this.onExistingBookingParticipantLeaveClick}>Leave tournament</button>
              <button
                type="button"
                className="isPrimary"
                onClick={() => {
                  this.setState({ isExistingBookingModalOpen: false }, () => {
                    history.push({ pathname: '/tournaments/teams', search: `tournamentId=${invite.tournamentId}` });
                  });
                }}
              >
                Open teams
              </button>
            </footer>
          </section>
        </SimpleModal>
      );
    }

    if (existingBookingMode === 'WAITING_LIST') {
      return (
        <StatusModal
          isOpen={isExistingBookingModalOpen}
          status="info"
          title="Already on waiting list"
          message={`Your school is already on the waiting list for ${tournament.name}.`}
          lines={[
            'You can open your waiting list tournaments to check your current position.',
            'If a place becomes available and the organisers move your school into the participant list, you will receive a notification.',
            'If you no longer want this booking, you can withdraw it.'
          ]}
          primaryButtonText="Open waiting list"
          onPrimaryClick={() => {
            this.setState({ isExistingBookingModalOpen: false }, () => {
              history.push({ pathname: '/tournaments/waitingList' });
            });
          }}
          cancelButtonText="Withdraw booking"
          onClose={this.onExistingBookingWaitingListLeaveClick}
          onCloseIconClick={() => this.setState({ isExistingBookingModalOpen: false })}
        />
      );
    }

    if (existingBookingMode === 'REJECTED_PARTICIPANT') {
      return (
        <StatusModal
          isOpen={isExistingBookingModalOpen}
          status="warning"
          title="Booking status needs attention"
          message={`Your invite for ${tournament.name} is marked as rejected, but your school is still in the participant list.`}
          lines={[
            'You can open your tournament teams to continue managing your participation.',
            'If your school should no longer take part, you can leave the tournament here.'
          ]}
          primaryButtonText="Open teams"
          onPrimaryClick={() => {
            this.setState({ isExistingBookingModalOpen: false }, () => {
              history.push({
                pathname: '/tournaments/teams',
                search: `tournamentId=${invite.tournamentId}`
              });
            });
          }}
          cancelButtonText="Leave tournament"
          onClose={this.onExistingBookingParticipantLeaveClick}
          onCloseIconClick={() => this.setState({ isExistingBookingModalOpen: false })}
        />
      );
    }

    return null;
  }

  renderNotInUnionModal(): React.ReactNode {
    const { isNotInUnionModalOpen, notInUnionMessage } = this.state;
    const { history } = this.props;

    const closeModal = () => {
      this.setState({ isNotInUnionModalOpen: false }, () => {
        history.push({ pathname: '/dashboard' });
      });
    };

    return (
      <SimpleModal
        isOpen={isNotInUnionModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentRegistrationUnavailableHost"
        dialogStyle={{ maxWidth: 480 }}
        onBackdropClick={closeModal}
      >
        <section
          className="tournamentRegistrationUnavailable"
          role="alertdialog"
          aria-label="Registration not available"
        >
          <header>
            <span className="tournamentRegistrationUnavailable__icon" aria-hidden="true">
              !
            </span>
            <div>
              <h2>Registration not available</h2>
              <p>This tournament is not currently available to your school.</p>
            </div>
            <button type="button" onClick={closeModal} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentRegistrationUnavailable__body">{notInUnionMessage}</div>
          <footer>
            <button type="button" onClick={closeModal}>
              Back to dashboard
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderLoadErrorModal(): React.ReactNode {
    const { history } = this.props;
    const closeModal = () => this.setState({ isLoadErrorModalOpen: false }, () => history.push('/dashboard'));
    return (
      <StatusModal
        isOpen={this.state.isLoadErrorModalOpen}
        status="error"
        title="Booking could not be opened"
        message={this.state.loadErrorMessage}
        compact
        cancelButtonText="Back to dashboard"
        onClose={closeModal}
        onCloseIconClick={closeModal}
      />
    );
  }

  acceptTournamentInviteWaitingList = () => {
    if (!this.canSubmitEventSelection()) return;
    const { user, history } = this.props;
    const { invite } = this.state;

    if (!invite || !invite.tournament) {
      this.setState({
        isLoading: false
      });
      return;
    }

    const { id: inviteId } = invite;

    this.setState({
      isLoading: true
    });

    acceptInvite(user, inviteId, this.getEventSelectionPayload()).then(res => {
      this.setState({
        isLoading: false,
        isTournamentInviteWaitingListModalOpen: false,
        isAcceptTournamentInviteWithAutoEnrollmentModalOpen: false,
        isTournamentInviteInfoModalOpen: true,
        isBookingConfirmedAsWaitingList: true
      });
    }).catch(this.onBookingError);
  };

  onTournamentInviteWaitingListModalCloseClick = () => {
    this.setState({
      isTournamentInviteWaitingListModalOpen: false,
      isAcceptTournamentInviteWithAutoEnrollmentModalOpen: true
    });
  };

  onCloseWithdrawWaitingListSuccessModal = () => {
    const { history } = this.props;

    this.setState(
      {
        isWithdrawWaitingListSuccessModalOpen: false
      },
      () => {
        history.push({
          pathname: '/dashboard'
        });
      }
    );
  };

  renderTournamentInviteWaitingListModalOpen(): React.ReactNode {
    const { isTournamentInviteWaitingListModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isTournamentInviteWaitingListModalOpen}
        title={'Join waiting list'}
        body={
          'You have not selected any teams. Your school will not be added to the participant list and will instead be placed on the waiting list.'
        }
        buttonText={'Join waiting list'}
        buttonCancelText={'Back'}
        onButtonClick={this.acceptTournamentInviteWaitingList}
        onCloseClick={this.onTournamentInviteWaitingListModalCloseClick}
      />
    );
  }

  renderTournamentInviteInfoModalOpen(): React.ReactNode {
    const { isTournamentInviteInfoModalOpen, invite, isBookingConfirmedAsWaitingList } = this.state;
    const { history } = this.props;

    if (!invite || !invite.tournament) {
      return null;
    }

    const { tournament } = invite;

    if (isBookingConfirmedAsWaitingList) {
      return (
        <TournamentWaitingListSuccessModal
          isOpen={isTournamentInviteInfoModalOpen}
          tournamentName={tournament.name}
          onClose={() => {
            this.setState(
              {
                isTournamentInviteInfoModalOpen: false,
                isBookingConfirmedAsWaitingList: false
              },
              () => {
                history.push({
                  pathname: '/tournaments/waitingList'
                });
              }
            );
          }}
        />
      );
    }

    return (
      <TournamentBookingSuccessModal
        isOpen={isTournamentInviteInfoModalOpen}
        tournamentName={tournament.name}
        onClose={() => {
          this.setState({ isTournamentInviteInfoModalOpen: false, isBookingConfirmedAsWaitingList: false }, () => {
            history.push({ pathname: '/tournaments/upcoming' });
          });
        }}
      />
    );
  }

  renderWithdrawWaitingListSuccessModal(): React.ReactNode {
    const { isWithdrawWaitingListSuccessModalOpen } = this.state;

    return (
      <StatusModal
        isOpen={isWithdrawWaitingListSuccessModalOpen}
        status="success"
        title="Booking withdrawn"
        message="Your school has been successfully removed from the waiting list."
        onClose={this.onCloseWithdrawWaitingListSuccessModal}
      />
    );
  }

  render() {
    const {
      isLoading,
      isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen,
      isAcceptTournamentInviteWithAutoEnrollmentModalOpen,
      isTournamentInviteWaitingListModalOpen,
      isTournamentInviteInfoModalOpen,
      isExistingBookingModalOpen,
      isWithdrawWaitingListSuccessModalOpen,
      isLoadErrorModalOpen
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isAcceptTournamentInviteWithoutAutoEnrollmentModalOpen ||
      isAcceptTournamentInviteWithAutoEnrollmentModalOpen ||
      isTournamentInviteInfoModalOpen ||
      isTournamentInviteWaitingListModalOpen ||
      isExistingBookingModalOpen ||
      isWithdrawWaitingListSuccessModalOpen ||
      isLoadErrorModalOpen
        ? 'mt-3 modal-open'
        : 'mt-3';

    return (
      <div className={classes}>
        {this.renderAcceptTournamentInviteWithoutAutoEnrollmentModal()}
        {this.renderAcceptTournamentInviteWithAutoEnrollmentModal()}
        {this.renderTournamentInviteWaitingListModalOpen()}
        {this.renderTournamentInviteInfoModalOpen()}
        {this.renderExistingBookingModal()}
        {this.renderWithdrawWaitingListSuccessModal()}
        {this.renderNotInUnionModal()}
        {this.renderLoadErrorModal()}
      </div>
    );
  }
}
